import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel,
  FormControl, RadioGroup, FormControlLabel, Radio,
  Checkbox, FormGroup, Typography, Box, TextField, MenuItem,
  Chip, Stack, Alert, CircularProgress,
} from '@mui/material'
import { useReportDataSources, type DataSource } from '../../hooks/useReportBuilder'
import { useGenerateReport, useCreateSchedule } from '../../hooks/useReports'
import { useDevices, type Device } from '../../hooks/useDevices'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

const STEPS_KEYS = ['reports.stepDataSource', 'reports.stepColumns', 'reports.stepFilters', 'reports.stepGrouping', 'reports.stepGenerate']

interface Props {
  open: boolean
  onClose: () => void
}

export default function ReportBuilderDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const { data: sources = [], isLoading } = useReportDataSources()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)
  const generate = useGenerateReport()
  const schedule = useCreateSchedule()

  const [step, setStep] = useState(0)
  const [selectedSource, setSelectedSource] = useState<DataSource | null>(null)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])
  const [dateRangeDays, setDateRangeDays] = useState(7)
  const [groupBy, setGroupBy] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [reportName, setReportName] = useState('')

  const handleSourceChange = (key: string) => {
    const src = sources.find((s) => s.key === key) ?? null
    setSelectedSource(src)
    setSelectedColumns([])
    setGroupBy('')
    setSortBy('')
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const handleGenerate = async () => {
    if (!selectedSource) return
    try {
      await generate.mutateAsync({
        report_type: 'custom',
        name: reportName || `${t('reports.customReport')} ${selectedSource.label}`,
        config: {
          data_source: selectedSource.key,
          columns: selectedColumns,
          device_ids: selectedDevices.length > 0 ? selectedDevices : undefined,
          date_range_days: dateRangeDays,
          group_by: groupBy || undefined,
          sort_by: sortBy || undefined,
          sort_order: sortOrder,
        },
      })
      onClose()
    } catch {
      // error shown via generate.error
    }
  }

  const STEPS = STEPS_KEYS.map((k) => t(k))

  const canNext = () => {
    switch (step) {
      case 0: return !!selectedSource
      case 1: return selectedColumns.length > 0
      case 2: return true
      case 3: return true
      default: return false
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('reports.customReportBuilder')}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {isLoading && <CircularProgress />}

        {/* Step 0: Data Source */}
        {step === 0 && (
          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('reports.selectDataSource')}</Typography>
            <RadioGroup value={selectedSource?.key ?? ''} onChange={(e) => handleSourceChange(e.target.value)}>
              {sources.map((s) => (
                <FormControlLabel key={s.key} value={s.key} control={<Radio />} label={s.label} />
              ))}
            </RadioGroup>
          </FormControl>
        )}

        {/* Step 1: Columns */}
        {step === 1 && selectedSource && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('reports.selectColumns')}</Typography>
            <FormGroup>
              {selectedSource.columns.map((col) => (
                <FormControlLabel key={col.key}
                  control={<Checkbox checked={selectedColumns.includes(col.key)} onChange={() => toggleColumn(col.key)} />}
                  label={<Box><Typography variant="body2">{col.label}</Typography><Typography variant="caption" color="text.secondary">{col.type}</Typography></Box>}
                />
              ))}
            </FormGroup>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {selectedColumns.map((k) => {
                const col = selectedSource.columns.find((c) => c.key === k)
                return <Chip key={k} label={col?.label ?? k} size="small" onDelete={() => toggleColumn(k)} />
              })}
            </Stack>
          </Box>
        )}

        {/* Step 2: Filters */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2">{t('reports.stepFilters')}:</Typography>
            <TextField select label={t('reports.dateRange')} value={dateRangeDays}
              onChange={(e) => setDateRangeDays(Number(e.target.value))} size="small">
              {[1, 3, 7, 14, 30, 60, 90].map((d) => (
                <MenuItem key={d} value={d}>{t('reports.lastDays', { count: d })}</MenuItem>
              ))}
            </TextField>
            <TextField select label={t('reports.filterDevices')} value="" size="small"
              SelectProps={{ multiple: true, value: selectedDevices,
                onChange: (e) => setSelectedDevices(e.target.value as string[]),
                renderValue: (sel) => `${(sel as string[]).length} ${t('reports.devicesSelected')}`,
              }}>
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Step 3: Grouping & Sorting */}
        {step === 3 && selectedSource && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2">{t('reports.groupingAndSorting')}</Typography>
            {selectedSource.group_by_options.length > 0 && (
              <TextField select label={t('reports.groupBy')} value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)} size="small">
                <MenuItem value="">{t('common.all')}</MenuItem>
                {selectedSource.group_by_options.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField select label={t('reports.sortBy')} value={sortBy}
              onChange={(e) => setSortBy(e.target.value)} size="small">
              <MenuItem value="">{t('reports.sortDefault')}</MenuItem>
              {selectedColumns.map((k) => {
                const col = selectedSource.columns.find((c) => c.key === k)
                return <MenuItem key={k} value={k}>{col?.label ?? k}</MenuItem>
              })}
            </TextField>
            <TextField select label={t('reports.sortOrder')} value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)} size="small">
              <MenuItem value="asc">{t('reports.ascending')}</MenuItem>
              <MenuItem value="desc">{t('reports.descending')}</MenuItem>
            </TextField>
            <TextField label={t('common.name')} value={reportName}
              onChange={(e) => setReportName(e.target.value)} size="small"
              placeholder={t('reports.reportNamePlaceholder')} />
          </Box>
        )}

        {/* Step 4: Generate */}
        {step === 4 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('reports.reviewAndGenerate')}</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('reports.sourceLabel')}: <strong>{selectedSource?.label}</strong> |
              {t('reports.columnsLabel')}: <strong>{selectedColumns.length}</strong> |
              {t('reports.dateRange')}: <strong>{dateRangeDays} {t('reports.days')}</strong>
              {selectedDevices.length > 0 && <> | {t('nav.devices')}: <strong>{selectedDevices.length}</strong></>}
            </Alert>
            {generate.isError && (
              <Alert severity="error" sx={{ mb: 1 }}>{t('reports.generateError')}</Alert>
            )}
            {generate.isSuccess && (
              <Alert severity="success" sx={{ mb: 1 }}>{t('reports.generated')} {t('reports.checkHistory')}</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>{t('common.back')}</Button>}
        {step < STEPS.length - 1 && (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!canNext()}>
            {t('reports.next')}
          </Button>
        )}
        {step === STEPS.length - 1 && (
          <Button variant="contained" onClick={handleGenerate}
            disabled={generate.isPending || generate.isSuccess}>
            {generate.isPending ? t('reports.generating') : t('reports.generateNow')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
